import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const WHATSAPP_URL =
  "https://wa.me/48512929641?text=" +
  encodeURIComponent("Dzień dobry, mam pytanie o dzierżawę nieruchomości pod maszt BTS.");

const NAV_LINKS = [
  ["dla-kogo", "Dla kogo"],
  ["jak-to-dziala", "Jak to działa"],
  ["dlaczego-my", "Dlaczego my"],
  ["faq", "FAQ"],
  ["kontakt", "Kontakt"],
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const sections = NAV_LINKS
      .map(([id]) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200/70"
          : "bg-transparent"
      }`}
      data-testid="navbar"
      aria-label="Główna nawigacja"
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-md"
          data-testid="navbar-logo"
          aria-label="Telecom BTS — strona główna"
        >
          <img
            src={scrolled ? "/logo/logo-wordmark-dark.svg" : "/logo/logo-wordmark-light.svg"}
            alt="Telecom BTS"
            width="240"
            height="48"
            className="h-7 sm:h-8 w-auto"
          />
        </button>

        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(([id, label]) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => scrollTo(id)}
                aria-current={isActive ? "location" : undefined}
                className={`text-sm font-medium transition-colors relative py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-sm ${
                  scrolled
                    ? isActive ? "text-emerald-700" : "text-slate-700 hover:text-emerald-700"
                    : isActive ? "text-emerald-300" : "text-white/90 hover:text-emerald-300"
                }`}
                data-testid={`nav-link-${id}`}
              >
                {label}
                {isActive && (
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-0.5 rounded-full ${
                      scrolled ? "bg-emerald-600" : "bg-emerald-400"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
          <div className="flex items-center gap-2">
            <a
              href="tel:+48512929641"
              className={`flex items-center gap-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-md px-1 ${
                scrolled ? "text-slate-700 hover:text-emerald-700" : "text-white/90 hover:text-emerald-300"
              }`}
              aria-label="Zadzwoń do nas"
            >
              <Phone className="w-4 h-4" aria-hidden="true" />
              <span className="tabular-nums hidden lg:inline">512 929 641</span>
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center w-8 h-8 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                scrolled
                  ? "text-[#25D366] hover:bg-emerald-50"
                  : "text-[#25D366] hover:bg-white/10"
              }`}
              aria-label="Napisz przez WhatsApp"
            >
              <WhatsAppIcon className="w-4 h-4" />
            </a>
          </div>
          <button
            type="button"
            onClick={() => scrollTo("hero-form")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 shadow-md shadow-emerald-900/20 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            data-testid="nav-cta-button"
          >
            Sprawdź czynsz
          </button>
        </div>

        <button
          type="button"
          className={`md:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
            scrolled ? "text-slate-800" : "text-white"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label={menuOpen ? "Zamknij menu" : "Otwórz menu"}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {menuOpen && (
        <div
          id="mobile-menu"
          ref={mobileMenuRef}
          className="md:hidden bg-white border-t border-slate-200 px-6 py-5 space-y-1 shadow-lg"
        >
          {NAV_LINKS.map(([id, label]) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => scrollTo(id)}
                aria-current={isActive ? "location" : undefined}
                className={`block w-full text-left text-sm font-medium transition-colors py-3 px-2 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  isActive
                    ? "text-emerald-700 bg-emerald-50"
                    : "text-slate-700 hover:text-emerald-700 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => scrollTo("hero-form")}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-3 rounded-full transition-colors mt-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
          >
            Sprawdź czynsz
          </button>
        </div>
      )}
    </nav>
  );
}
